import { useTranslation } from 'react-i18next';
import { HpIcon } from '@hp/ui';
import { PanelHead } from './panel';
import type { Fmt } from './format';
import type { Report } from './use-report';

type Insight = Report['insights'][number];

/** Rule-based recommendations (`pa-insights`): each card names the rule, the channel and the numbers behind it. */
export function Insights({ insights, fmt }: { insights: Insight[]; fmt: Fmt }) {
  const { t } = useTranslation(['performance', 'common']);
  const values = (i: Insight): Record<string, string> => {
    const v = i.values;
    return {
      channel: i.channel ? t(`channel.${i.channel}`) : '',
      costPerMeeting: fmt.czk(v.costPerMeeting ?? 0),
      roas: fmt.times(v.roas ?? 0),
      meetings: fmt.int(v.meetings ?? 0),
      noContact: fmt.int(v.noContact ?? 0),
      leads: fmt.int(v.leads ?? 0),
      share: fmt.pct(v.share ?? 0, 0),
      spend: fmt.compactCzk(v.spend ?? 0),
      qualified: fmt.int(v.qualified ?? 0),
      cpql: fmt.czk(v.cpql ?? 0),
      average: fmt.czk(v.average ?? 0),
      forecast: fmt.compactCzk(v.forecast ?? 0),
      plan: fmt.compactCzk(v.plan ?? 0),
      over: fmt.pct(v.over ?? 0, 0),
    };
  };
  return (
    <article className="pa-panel" id="pa-insights">
      <PanelHead title={t('dashboard.insights.title')} subtitle={t('dashboard.insights.subtitle')}>
        <span className="pa-priority-count">
          <HpIcon name="checkSquare" />
          <span>{t('dashboard.insights.count', { count: insights.length })}</span>
        </span>
      </PanelHead>
      <div className="pa-insights">
        {insights.length === 0 && <p className="pa-empty">{t('dashboard.insights.empty')}</p>}
        {insights.map((i, idx) => (
          <article key={`${i.code}-${i.channel ?? idx}`} className={`pa-insight ${i.tone}`}>
            <span className={`pa-insight-signal ${i.tone}`}>
              <span className="pa-insight-symbol" aria-hidden="true">
                <HpIcon name={i.tone === 'good' ? 'trendingUp' : i.tone === 'warning' ? 'search' : 'sliders'} />
              </span>
              <span>{t(`dashboard.insights.tone.${i.tone}`)}</span>
            </span>
            <h3>{t(`dashboard.insights.rule.${i.code}.title`, { replace: values(i) })}</h3>
            <p>{t(`dashboard.insights.rule.${i.code}.text`, { replace: values(i) })}</p>
          </article>
        ))}
      </div>
      <p className="pa-footnote">{t('dashboard.insights.footnote')}</p>
    </article>
  );
}
